import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { PageHeader } from 'components/PageHeader';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  selectAseTplFieldGroup,
  selectAseFieldGroupStats,
  selectAseFieldInstancesCountForGroup,
  selectAseIsEditable,
} from 'store/assessments/selectors';
import { InstanceParentParams } from 'store/assessments/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASEREPEATABLE],
  Screens.ASEREPEATABLE_LIST
>;

export const RepeatableListScreenHeader: React.FC<{
  navigation: Props['navigation'];
  groupId: string;
  parent: InstanceParentParams;
}> = ({ navigation, groupId, parent }) => {
  const dispatch = useDispatch();

  const isEditable = useSelector(selectAseIsEditable);
  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));

  // Get total number of instances in asset and in assessment
  const totalInstanceCount = useSelector((state) =>
    selectAseFieldInstancesCountForGroup(state, {
      groupId,
      parent: null,
      inAssessmentOnly: false,
    }),
  );

  const groupStats = useSelector((state) =>
    selectAseFieldGroupStats(state, {
      groupId,
      parent,
      inAssessmentOnly: true,
    }),
  );

  if (!group) {
    return null;
  }

  const unaddedLinkedCount = isEditable
    ? parent
      ? groupStats.childInstancesCount - groupStats.assessmentInstancesCount
      : 0
    : 0;

  const unaddedOtherCount = isEditable
    ? parent
      ? totalInstanceCount - groupStats.childInstancesCount
      : groupStats.allInstancesCount - groupStats.assessmentInstancesCount
    : 0;

  return (
    <PageHeader
      onPressBackButton={navigation.goBack}
      title={
        parent
          ? unaddedLinkedCount
            ? `${pluralise(
                groupStats.assessmentInstancesCount,
                '1',
                '{x}',
              )}/${pluralise(
                groupStats.childInstancesCount,
                `1 ${group.calculatedInfo.singularName}`,
                `{x} ${group.name}`,
              )}`
            : pluralise(
                groupStats.assessmentInstancesCount,
                `1 ${group.calculatedInfo.singularName}`,
                `{x} ${group.name}`,
              )
          : pluralise(
              groupStats.assessmentInstancesCount,
              `1 ${group.calculatedInfo.singularName}`,
              `{x} ${group.name}`,
              `No ${group.name}`,
            )
      }
      subtitle={
        isEditable && unaddedOtherCount
          ? pluralise(
              unaddedOtherCount,
              '{x} Existing Available',
              '{x} Existing Available',
              '',
            )
          : undefined
      }
      iconButtonRight={
        isEditable && !unaddedOtherCount
          ? {
              icon: ICONS.PlusLight,
              onPress: () =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.AseFieldInstance,
                    params: { groupId, instanceId: undefined, parent },
                  }),
                ),
            }
          : undefined
      }
      menuRight={
        isEditable && !!unaddedOtherCount
          ? {
              iconButtonConfig: {
                icon: ICONS.PlusLight,
              },
              options: [
                {
                  text: 'Add New',
                  icon: ICONS.PlusLight,
                  onPress: () =>
                    dispatch(
                      NAVIGATE_MODAL({
                        screen: ModalScreens.AseFieldInstance,
                        params: {
                          groupId,
                          instanceId: undefined,
                          parent,
                        },
                      }),
                    ),
                },
                {
                  text: 'Add Existing',
                  subText: `${pluralise(unaddedOtherCount, '{x} Available', '{x} Available')}`,
                  icon: ICONS.PlusLight,
                  onPress: () =>
                    dispatch(
                      NAVIGATE_MODAL({
                        screen: ModalScreens.ASEREPEATABLEINSTANCENEWFROMLIST,
                        params: { groupId, parent },
                      }),
                    ),
                },
              ],
            }
          : undefined
      }
      showSidebarButtonDisabled
    />
  );
};
